import { useEffect, useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { listMatchRequests, updateMatchRequest, adminLogout, getAdminUser, getToken } from "../lib/api";
import { isPasscodeUnlocked, clearPasscode } from "./AdminLogin";
import { INVESTMENTS } from "../mock";
import { useToast } from "../hooks/use-toast";
import {
  ArrowRight,
  Loader2,
  RefreshCw,
  Inbox,
  TrendingUp,
  Users,
  PoundSterling,
  Search,
  LogOut,
  ChevronDown,
  ChevronUp,
  MessageSquare,
  Send,
  CheckCircle2,
  X,
} from "lucide-react";

const STATUS_META = {
  new:       { label: "New",       cls: "bg-[#dbeafe] text-[#1e40af]" },
  contacted: { label: "Contacted", cls: "bg-[#fde68a] text-[#92400e]" },
  qualified: { label: "Qualified", cls: "bg-[#fed7aa] text-[#9a3412]" },
  converted: { label: "Customer",  cls: "bg-[#16d196] text-[#0d1a2e]" },
  lost:      { label: "Lost",      cls: "bg-[#fecaca] text-[#991b1b]" },
};
const STATUS_OPTIONS = ["new", "contacted", "qualified", "converted", "lost"];

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function typeNames(slugs = []) {
  return slugs.map((s) => INVESTMENTS.find((i) => i.slug === s)?.name || s).join(", ");
}

export default function Admin() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [tab, setTab] = useState("leads");
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState({});
  const user = getAdminUser();

  // Redirect to login if passcode not unlocked OR no JWT
  useEffect(() => {
    if (!isPasscodeUnlocked() || !getToken()) {
      navigate("/admin/login", { replace: true });
    }
  }, [navigate]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listMatchRequests();
      setLeads(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate("/admin/login", { replace: true });
        return;
      }
      setError("Could not load submissions. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const customers = useMemo(() => leads.filter((l) => l.status === "converted"), [leads]);
  const rows = tab === "customers" ? customers : leads;

  const filtered = useMemo(() => rows.filter((r) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (
      r.name?.toLowerCase().includes(q) ||
      r.email?.toLowerCase().includes(q) ||
      r.phone?.includes(q) ||
      typeNames(r.types).toLowerCase().includes(q)
    );
  }), [rows, query]);

  // Stats
  const totalLeads = leads.length;
  const last7 = leads.filter((r) => Date.now() - new Date(r.created_at).getTime() < 7 * 86400000).length;
  const conv = leads.filter((r) => r.status === "converted").length;
  const convRate = totalLeads ? Math.round((conv / totalLeads) * 100) : 0;
  const topType = (() => {
    const counts = {};
    leads.forEach((r) => (r.types || []).forEach((t) => (counts[t] = (counts[t] || 0) + 1)));
    const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    if (!sorted.length) return "—";
    return INVESTMENTS.find((i) => i.slug === sorted[0][0])?.name || sorted[0][0];
  })();

  const handleStatus = async (id, newStatus) => {
    try {
      const updated = await updateMatchRequest(id, { status: newStatus });
      setLeads((ls) => ls.map((l) => (l.id === id ? updated : l)));
      toast({ title: "Status updated", description: `Marked as ${STATUS_META[newStatus]?.label || newStatus}.` });
    } catch {
      toast({ title: "Update failed", description: "Please try again." });
    }
  };

  const handleAddNote = async (id, text) => {
    if (!text?.trim()) return;
    try {
      const updated = await updateMatchRequest(id, { note: text.trim() });
      setLeads((ls) => ls.map((l) => (l.id === id ? updated : l)));
      toast({ title: "Note added" });
      return true;
    } catch {
      toast({ title: "Couldn't add note" });
      return false;
    }
  };

  const signOut = () => {
    adminLogout();
    clearPasscode();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <section className="relative bg-[#0d1a2e] text-white overflow-hidden">
        <div className="absolute inset-0 bg-grid-dark opacity-30 pointer-events-none" />
        <div className="absolute -top-20 right-0 w-[36rem] h-[36rem] glow-emerald pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-10 lg:pt-12 pb-10 lg:pb-12">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="inline-flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.18em] text-[#16d196]">
                <span className="w-1.5 h-1.5 rounded-full bg-[#16d196]" /> Admin portal
              </div>
              <h1 className="mt-3 text-[28px] sm:text-[36px] lg:text-[44px] font-extrabold leading-[1.05] tracking-[-0.025em]">
                Leads &amp; <span className="text-[#16d196]">customers.</span>
              </h1>
              <p className="mt-2 text-white/65 text-[14px] lg:text-[15px] max-w-2xl">
                Manage every match-request submitted via the platform. Update status,
                add notes, and convert qualified leads to customers.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={load}
                disabled={loading}
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white rounded-full h-10 px-4 text-[13px] font-bold transition-colors disabled:opacity-50"
              >
                {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" strokeWidth={2.4} />}
                Refresh
              </button>
              <button
                onClick={signOut}
                className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 text-white/85 hover:text-white rounded-full h-10 px-4 text-[13px] font-bold transition-colors"
              >
                <LogOut className="w-3.5 h-3.5" strokeWidth={2.4} />
                Sign out
              </button>
            </div>
          </div>

          {user?.email && (
            <div className="mt-4 text-[12px] text-white/55">
              Signed in as <span className="text-white font-semibold">{user.email}</span>
            </div>
          )}
        </div>
      </section>

      <section className="py-8 lg:py-10 bg-[#f7faf9] border-b border-[#0d1a2e]/8">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-5">
          <StatCard Icon={Inbox} label="Total leads" value={totalLeads} />
          <StatCard Icon={TrendingUp} label="Last 7 days" value={last7} />
          <StatCard Icon={Users} label="Customers" value={conv} sub={`${convRate}% conversion`} />
          <StatCard Icon={PoundSterling} label="Top interest" value={topType} small />
        </div>
      </section>

      <section className="py-8 lg:py-10 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          {/* Tabs */}
          <div className="flex items-center gap-2 mb-6">
            <TabBtn active={tab === "leads"} onClick={() => setTab("leads")}>
              Leads <span className="opacity-60">({leads.length})</span>
            </TabBtn>
            <TabBtn active={tab === "customers"} onClick={() => setTab("customers")}>
              Customers <span className="opacity-60">({customers.length})</span>
            </TabBtn>
          </div>

          <div className="flex items-center gap-3 mb-6">
            <div className="flex-1 relative">
              <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#0d1a2e]/45" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by name, email, phone or investment type…"
                className="w-full h-11 lg:h-12 bg-[#f7faf9] border border-[#0d1a2e]/10 rounded-full pl-11 pr-4 text-[14px] focus:outline-none focus:border-[#16d196]"
              />
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-[13.5px] text-red-700 mb-5">
              {error}
            </div>
          )}

          {loading && rows.length === 0 ? (
            <div className="flex justify-center py-16">
              <Loader2 className="w-6 h-6 text-[#16d196] animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <EmptyState tab={tab} query={query} hasAny={rows.length > 0} />
          ) : (
            <div className="space-y-2">
              {filtered.map((r) => (
                <LeadCard
                  key={r.id}
                  row={r}
                  expanded={!!expanded[r.id]}
                  toggle={() => setExpanded((e) => ({ ...e, [r.id]: !e[r.id] }))}
                  onStatus={(s) => handleStatus(r.id, s)}
                  onAddNote={(t) => handleAddNote(r.id, t)}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}

function TabBtn({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 lg:px-5 h-10 rounded-full text-[13.5px] font-bold border transition-colors ${
        active
          ? "bg-[#0d1a2e] text-white border-[#0d1a2e]"
          : "bg-white text-[#0d1a2e]/70 border-[#0d1a2e]/15 hover:border-[#16d196] hover:text-[#0d1a2e]"
      }`}
    >
      {children}
    </button>
  );
}

function StatCard({ Icon, label, value, sub, small }) {
  return (
    <div className="bg-white border border-[#0d1a2e]/8 rounded-2xl p-5">
      <div className="flex items-center gap-2.5">
        <div className="w-9 h-9 rounded-lg bg-[#e3f7ee] flex items-center justify-center">
          <Icon className="w-4 h-4 text-[#0d1a2e]" strokeWidth={2.2} />
        </div>
        <div className="text-[10.5px] uppercase tracking-[0.14em] text-[#0d1a2e]/55 font-bold">
          {label}
        </div>
      </div>
      <div className={`mt-3 ${small ? "text-[18px]" : "text-[28px] lg:text-[32px]"} font-extrabold text-[#0d1a2e] tracking-[-0.02em] leading-none`}>
        {value}
      </div>
      {sub && <div className="mt-2 text-[11.5px] text-[#0d1a2e]/55 font-semibold">{sub}</div>}
    </div>
  );
}

function EmptyState({ tab, query, hasAny }) {
  return (
    <div className="bg-[#f7faf9] border border-[#0d1a2e]/8 rounded-2xl p-12 text-center">
      <Inbox className="w-8 h-8 text-[#0d1a2e]/40 mx-auto" />
      <p className="mt-3 text-[#0d1a2e]/55 text-[14px]">
        {hasAny && query
          ? `No ${tab} match your search.`
          : tab === "customers"
          ? "No customers yet — mark a lead as converted to add them here."
          : "No leads yet — they'll appear here as soon as a visitor submits the match form."}
      </p>
      {!hasAny && (
        <Link to="/apply" className="mt-5 inline-flex items-center gap-1.5 text-[13.5px] font-bold text-[#16d196]">
          Try the /apply form <ArrowRight className="w-4 h-4" strokeWidth={2.6} />
        </Link>
      )}
    </div>
  );
}

function LeadCard({ row, expanded, toggle, onStatus, onAddNote }) {
  const [statusOpen, setStatusOpen] = useState(false);
  const [noteInput, setNoteInput] = useState("");
  const [adding, setAdding] = useState(false);
  const meta = STATUS_META[row.status] || STATUS_META.new;

  const submitNote = async () => {
    if (!noteInput.trim()) return;
    setAdding(true);
    const ok = await onAddNote(noteInput);
    setAdding(false);
    if (ok) setNoteInput("");
  };

  return (
    <div className="bg-white border border-[#0d1a2e]/10 rounded-2xl overflow-hidden">
      <div className="p-4 lg:p-5 grid grid-cols-1 lg:grid-cols-12 gap-3 lg:gap-4 lg:items-center">
        <div className="lg:col-span-3">
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-full bg-[#0d1a2e] text-white flex items-center justify-center font-extrabold text-[12px] shrink-0">
              {(row.name || "?").split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="text-[14px] font-extrabold text-[#0d1a2e] truncate">{row.name}</div>
              <a href={`mailto:${row.email}`} className="text-[12px] text-[#16d196] hover:underline truncate block">{row.email}</a>
            </div>
          </div>
        </div>

        <div className="lg:col-span-2 text-[12.5px] text-[#0d1a2e]/75">
          <div className="text-[10px] uppercase tracking-[0.12em] text-[#0d1a2e]/45 font-bold">Received</div>
          <div className="mt-0.5 text-[13px] font-semibold text-[#0d1a2e]">{formatDate(row.created_at)}</div>
          <a href={`tel:${row.phone}`} className="text-[12px] text-[#0d1a2e]/65 hover:text-[#16d196]">{row.phone}</a>
        </div>

        <div className="lg:col-span-3">
          <div className="text-[10px] uppercase tracking-[0.12em] text-[#0d1a2e]/45 font-bold">Interest</div>
          <div className="mt-1 flex flex-wrap gap-1">
            {(row.types || []).slice(0, 2).map((t) => (
              <span key={t} className="inline-flex bg-[#e3f7ee] text-[#0d1a2e] text-[10.5px] font-bold rounded-full px-2 py-0.5">
                {INVESTMENTS.find((i) => i.slug === t)?.name || t}
              </span>
            ))}
            {(row.types || []).length > 2 && (
              <span className="text-[11px] text-[#0d1a2e]/60 font-bold">+{(row.types || []).length - 2}</span>
            )}
          </div>
          <div className="mt-1.5 text-[11.5px] text-[#0d1a2e]/55">
            {row.range || "—"} · {row.horizon || "—"} · {row.goal || "—"}
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="text-[10px] uppercase tracking-[0.12em] text-[#0d1a2e]/45 font-bold">Status</div>
          <div className="relative mt-1">
            <button
              onClick={() => setStatusOpen((s) => !s)}
              className={`inline-flex items-center gap-1 text-[11.5px] font-extrabold uppercase tracking-[0.08em] rounded-full px-3 py-1.5 ${meta.cls}`}
            >
              {meta.label}
              <ChevronDown className="w-3.5 h-3.5" strokeWidth={2.6} />
            </button>
            {statusOpen && (
              <div className="absolute z-20 top-full left-0 mt-1 min-w-[140px] bg-white border border-[#0d1a2e]/15 rounded-xl shadow-lg p-1">
                {STATUS_OPTIONS.map((s) => (
                  <button
                    key={s}
                    onClick={() => { onStatus(s); setStatusOpen(false); }}
                    className="w-full text-left px-3 py-2 text-[12.5px] font-semibold text-[#0d1a2e] hover:bg-[#f7faf9] rounded-lg flex items-center gap-2"
                  >
                    <span className={`inline-block w-2 h-2 rounded-full ${STATUS_META[s].cls.split(" ")[0].replace("bg-", "bg-")}`} />
                    {STATUS_META[s].label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-2 flex items-center gap-2 lg:justify-end">
          {row.status !== "converted" && (
            <button
              onClick={() => onStatus("converted")}
              className="inline-flex items-center gap-1 bg-[#16d196] hover:bg-[#13b884] text-[#0d1a2e] rounded-full h-8 px-3 text-[11.5px] font-bold"
              title="Mark as customer"
            >
              <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2.6} />
              Convert
            </button>
          )}
          <button
            onClick={toggle}
            className="inline-flex items-center gap-1 bg-[#0d1a2e]/5 hover:bg-[#0d1a2e]/10 text-[#0d1a2e] rounded-full h-8 px-3 text-[11.5px] font-bold"
          >
            <MessageSquare className="w-3.5 h-3.5" strokeWidth={2.4} />
            Notes {row.notes?.length ? `(${row.notes.length})` : ""}
            {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-[#0d1a2e]/8 bg-[#f7faf9] p-4 lg:p-5">
          {row.notes?.length > 0 ? (
            <ul className="space-y-2 mb-4">
              {row.notes.map((n, i) => (
                <li key={i} className="bg-white border border-[#0d1a2e]/8 rounded-xl px-3.5 py-2.5">
                  <div className="text-[13px] text-[#0d1a2e]">{n.text}</div>
                  <div className="mt-1 text-[11px] text-[#0d1a2e]/55">
                    {n.by} · {formatDate(n.at)}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-[12.5px] text-[#0d1a2e]/55 mb-3">No notes yet on this lead.</p>
          )}
          <div className="flex items-center gap-2">
            <input
              value={noteInput}
              onChange={(e) => setNoteInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") submitNote(); }}
              placeholder="Add a note (e.g. called, left voicemail)…"
              className="flex-1 h-10 bg-white border border-[#0d1a2e]/10 focus:border-[#16d196] outline-none rounded-full px-4 text-[13px]"
            />
            <button
              onClick={submitNote}
              disabled={adding || !noteInput.trim()}
              className="inline-flex items-center gap-1.5 bg-[#0d1a2e] hover:bg-black text-white rounded-full h-10 px-4 text-[12.5px] font-bold disabled:opacity-50"
            >
              {adding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" strokeWidth={2.4} />}
              Add
            </button>
            {noteInput && (
              <button onClick={() => setNoteInput("")} className="text-[#0d1a2e]/55 hover:text-[#0d1a2e]">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
